import { deriveSeed, type SeedSource } from "./seed.js";

export const SOLO_GARDEN_OPERATIONS = Object.freeze(["water", "shelter", "open"] as const);

export type SoloGardenOperation = (typeof SOLO_GARDEN_OPERATIONS)[number];
export type SoloGardenTurn = 0 | 1 | 2 | 3;
export type SoloGardenActionTurn = Exclude<SoloGardenTurn, 0>;
export type SoloGardenPoem = readonly [string, string, string];

export interface SoloGardenAction {
  readonly type: "tend";
  readonly turn: SoloGardenActionTurn;
  readonly operation: SoloGardenOperation;
}

export interface SoloGardenTend {
  readonly turn: SoloGardenActionTurn;
  readonly operation: SoloGardenOperation;
}

export interface SoloGardenCompletion {
  readonly text: string;
  readonly summary: string;
}

export interface SoloGardenState {
  readonly schemaVersion: 1;
  readonly mode: "solo-garden";
  readonly seed: number;
  readonly poem: SoloGardenPoem;
  readonly words: readonly string[];
  readonly turn: SoloGardenTurn;
  readonly tends: readonly SoloGardenTend[];
  readonly completion: SoloGardenCompletion | null;
}

const STATE_KEYS = Object.freeze([
  "schemaVersion",
  "mode",
  "seed",
  "poem",
  "words",
  "turn",
  "tends",
  "completion",
] as const);

const ACTION_KEYS = Object.freeze(["type", "turn", "operation"] as const);
const TEND_KEYS = Object.freeze(["turn", "operation"] as const);
const COMPLETION_KEYS = Object.freeze(["text", "summary"] as const);
const SOLO_SEED_NAMESPACE = "game/solo/v1" as const;

function readExactRecord(value: unknown, keys: readonly string[], label: string): readonly unknown[] {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new TypeError(`${label} must be a plain object`);
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${label} must be a plain object`);
  }
  const actualKeys = Reflect.ownKeys(value);
  if (
    actualKeys.length !== keys.length
    || actualKeys.some((key) => typeof key !== "string" || !keys.includes(key))
  ) {
    throw new TypeError(`${label} contains unknown or missing keys`);
  }
  return keys.map((key) => {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label}.${key} must be an enumerable data property`);
    }
    return descriptor.value as unknown;
  });
}

function readExactArray(value: unknown, expectedLength: number, label: string): readonly unknown[] {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) {
    throw new TypeError(`${label} must be an array`);
  }
  const indexKeys = Array.from({ length: expectedLength }, (_, index) => String(index));
  const expectedKeys = [...indexKeys, "length"];
  const actualKeys = Reflect.ownKeys(value);
  const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
  if (
    lengthDescriptor === undefined
    || lengthDescriptor.enumerable
    || !("value" in lengthDescriptor)
    || lengthDescriptor.value !== expectedLength
    || actualKeys.length !== expectedKeys.length
    || actualKeys.some((key) => typeof key !== "string" || !expectedKeys.includes(key))
  ) {
    throw new TypeError(`${label} must contain exactly ${expectedLength} items`);
  }
  return indexKeys.map((key) => {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !descriptor.enumerable || !("value" in descriptor)) {
      throw new TypeError(`${label}[${key}] must be an enumerable data property`);
    }
    return descriptor.value as unknown;
  });
}

function readBoundedArray(value: unknown, maximumLength: number, label: string): readonly unknown[] {
  if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) {
    throw new TypeError(`${label} must be an array`);
  }
  const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
  if (
    lengthDescriptor === undefined
    || lengthDescriptor.enumerable
    || !("value" in lengthDescriptor)
    || !Number.isInteger(lengthDescriptor.value)
    || lengthDescriptor.value < 0
    || lengthDescriptor.value > maximumLength
  ) {
    throw new TypeError(`${label} has invalid length`);
  }
  return readExactArray(value, lengthDescriptor.value as number, label);
}

function isWellFormedUnicode(value: string): boolean {
  for (let index = 0; index < value.length; index += 1) {
    const unit = value.charCodeAt(index);
    if (unit >= 0xd800 && unit <= 0xdbff) {
      if (index + 1 >= value.length) return false;
      const next = value.charCodeAt(index + 1);
      if (next < 0xdc00 || next > 0xdfff) return false;
      index += 1;
    } else if (unit >= 0xdc00 && unit <= 0xdfff) {
      return false;
    }
  }
  return true;
}

function readText(value: unknown, label: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new TypeError(`${label} must be a non-empty string`);
  }
  if (!isWellFormedUnicode(value)) throw new TypeError(`${label} must be well-formed Unicode`);
  return value;
}

function readSeedSource(value: unknown): SeedSource {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new RangeError("Solo Garden seed must be finite");
    return value;
  }
  if (typeof value === "string") {
    if (!isWellFormedUnicode(value)) {
      throw new TypeError("Solo Garden seed must be well-formed Unicode");
    }
    return value;
  }
  throw new TypeError("Solo Garden seed must be a string or number");
}

function readDerivedSeed(value: unknown): number {
  if (
    !Number.isInteger(value)
    || Object.is(value, -0)
    || (value as number) < 0
    || (value as number) > 0xffffffff
  ) {
    throw new RangeError("state.seed must be a uint32");
  }
  return value as number;
}

function readTurn(value: unknown, allowZero: boolean, label: string): SoloGardenTurn {
  if (!Number.isInteger(value) || (value as number) < (allowZero ? 0 : 1) || (value as number) > 3) {
    throw new RangeError(`${label} must be ${allowZero ? "between 0 and 3" : "between 1 and 3"}`);
  }
  return value as SoloGardenTurn;
}

function readOperation(value: unknown, label: string): SoloGardenOperation {
  if (
    typeof value !== "string"
    || !SOLO_GARDEN_OPERATIONS.some((operation) => operation === value)
  ) {
    throw new TypeError(`${label} must be water, shelter, or open`);
  }
  return value as SoloGardenOperation;
}

function readPoem(value: unknown): SoloGardenPoem {
  const lines = readExactArray(value, 3, "poem").map((line, index) =>
    readText(line, `poem[${index}]`),
  );
  return Object.freeze(lines as [string, string, string]);
}

function readAction(value: unknown, label = "action"): SoloGardenAction {
  const [type, turnValue, operationValue] = readExactRecord(value, ACTION_KEYS, label);
  if (type !== "tend") throw new TypeError(`${label}.type must be tend`);
  const turn = readTurn(turnValue, false, `${label}.turn`) as SoloGardenActionTurn;
  const operation = readOperation(operationValue, `${label}.operation`);
  return Object.freeze({ type, turn, operation });
}

function readTend(value: unknown, index: number): SoloGardenTend {
  const label = `state.tends[${index}]`;
  const [turnValue, operationValue] = readExactRecord(value, TEND_KEYS, label);
  const turn = readTurn(turnValue, false, `${label}.turn`) as SoloGardenActionTurn;
  if (turn !== index + 1) throw new TypeError(`${label}.turn is out of sequence`);
  return Object.freeze({ turn, operation: readOperation(operationValue, `${label}.operation`) });
}

function poemWords(poem: SoloGardenPoem): readonly string[] {
  return poem.flatMap((line) => line.split(/\s+/u).filter((word) => word.length > 0));
}

function shuffleWords(words: readonly string[], seed: number): readonly string[] {
  const shuffled = [...words];
  let randomState = seed;
  const next = (): number => {
    randomState = (randomState + 0x6d2b79f5) >>> 0;
    let value = randomState;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return (value ^ (value >>> 14)) >>> 0;
  };
  const bounded = (upperBound: number): number => {
    const limit = Math.floor(0x100000000 / upperBound) * upperBound;
    let value: number;
    do value = next(); while (value >= limit);
    return value % upperBound;
  };
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const target = bounded(index + 1);
    [shuffled[index], shuffled[target]] = [shuffled[target], shuffled[index]];
  }
  return Object.freeze(shuffled);
}

function orderingSeed(
  seed: number,
  poem: SoloGardenPoem,
  tends: readonly SoloGardenTend[],
): number {
  return deriveSeed(SOLO_SEED_NAMESPACE, JSON.stringify([seed, poem, tends]));
}

function buildState(
  seed: number,
  poemValue: SoloGardenPoem,
  tendValues: readonly SoloGardenTend[],
): SoloGardenState {
  const poem = Object.freeze([poemValue[0], poemValue[1], poemValue[2]] as const);
  const tends = Object.freeze(tendValues.map(({ turn, operation }) =>
    Object.freeze({ turn, operation }),
  ));
  const words = shuffleWords(poemWords(poem), orderingSeed(seed, poem, tends));
  const turn = tends.length as SoloGardenTurn;
  const completion = turn === 3
    ? Object.freeze({
      text: "The garden rests after three gentle tends.",
      summary: `${tends.map(({ operation }) => operation).join(", ")} tended ${words.length} words across three lines.`,
    })
    : null;
  return Object.freeze({
    schemaVersion: 1,
    mode: "solo-garden",
    seed,
    poem,
    words,
    turn,
    tends,
    completion,
  });
}

function sameStrings(actual: readonly string[], expected: readonly string[], label: string): void {
  if (
    actual.length !== expected.length
    || actual.some((value, index) => value !== expected[index])
  ) {
    throw new TypeError(`${label} does not match deterministic replay`);
  }
}

export function createSoloGarden(seedSource: unknown, poemValue: unknown): SoloGardenState {
  const seed = deriveSeed(SOLO_SEED_NAMESPACE, readSeedSource(seedSource));
  const poem = readPoem(poemValue);
  return buildState(seed, poem, []);
}

export function validateSoloGardenState(value: unknown): SoloGardenState {
  const [
    schemaVersion,
    mode,
    seedValue,
    poemValue,
    wordsValue,
    turnValue,
    tendsValue,
    completionValue,
  ] = readExactRecord(value, STATE_KEYS, "state");
  if (schemaVersion !== 1) throw new TypeError("state.schemaVersion must be 1");
  if (mode !== "solo-garden") throw new TypeError("state.mode must be solo-garden");
  const seed = readDerivedSeed(seedValue);
  const poem = readPoem(poemValue);
  const tendItems = readBoundedArray(tendsValue, 3, "state.tends");
  const tends = tendItems.map(readTend);
  const turn = readTurn(turnValue, true, "state.turn");
  if (turn !== tends.length) throw new TypeError("state.turn must match state.tends.length");

  const expected = buildState(seed, poem, tends);
  const words = readBoundedArray(wordsValue, poemWords(poem).length, "state.words").map(
    (word, index) => readText(word, `state.words[${index}]`),
  );
  sameStrings(words, expected.words, "state.words");

  if (expected.completion === null) {
    if (completionValue !== null) throw new TypeError("state.completion must be null before turn 3");
  } else {
    const [textValue, summaryValue] = readExactRecord(
      completionValue,
      COMPLETION_KEYS,
      "state.completion",
    );
    const text = readText(textValue, "state.completion.text");
    const summary = readText(summaryValue, "state.completion.summary");
    if (text !== expected.completion.text || summary !== expected.completion.summary) {
      throw new TypeError("state.completion does not match deterministic replay");
    }
  }
  return expected;
}

export function reduceSoloGarden(stateValue: unknown, actionValue: unknown): SoloGardenState {
  const state = validateSoloGardenState(stateValue);
  if (state.turn === 3) throw new RangeError("Solo Garden already has exactly three tends");
  const action = readAction(actionValue);
  if (action.turn !== state.turn + 1) {
    throw new RangeError(`action.turn must be ${state.turn + 1}`);
  }
  return buildState(state.seed, state.poem, [
    ...state.tends,
    Object.freeze({ turn: action.turn, operation: action.operation }),
  ]);
}

export function replaySoloGarden(
  seedSource: unknown,
  poemValue: unknown,
  actionValues: unknown,
): SoloGardenState {
  const actions = readBoundedArray(actionValues, 3, "actions").map((action, index) =>
    readAction(action, `actions[${index}]`),
  );
  return actions.reduce<SoloGardenState>(
    (state, action) => reduceSoloGarden(state, action),
    createSoloGarden(seedSource, poemValue),
  );
}
